
import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '@/i18n/i18n';
import { SupportedLanguage, supportedLanguages as allSupportedLanguages } from '@/hooks/useTranslation';

type LanguageContextType = {
  currentLanguage: string;
  setLanguage: (language: string) => void;
  supportedLanguages: SupportedLanguage[];
};

// Create a context with Danish as the default language
const LanguageContext = createContext<LanguageContextType>({
  currentLanguage: 'da',
  setLanguage: () => {},
  supportedLanguages: allSupportedLanguages,
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('da'); // Always use Danish

  // Modified setLanguage function to enforce Danish as the only language
  const setLanguage = (language: string) => {
    // Always use Danish regardless of the requested language
    const fixedLanguage = 'da';
    
    // Change the language in i18n
    i18n.changeLanguage(fixedLanguage);
    setCurrentLanguage(fixedLanguage);
    localStorage.setItem('language', fixedLanguage);
    
    // Set HTML lang attribute for accessibility and SEO
    document.documentElement.lang = fixedLanguage;
    
    console.log('Language forced to Danish');
    
    // Force React to re-render all translated components
    window.dispatchEvent(new Event('languageChanged'));
  };

  // Initialize language on component mount
  useEffect(() => {
    // Force Danish on mount
    i18n.changeLanguage('da');
    setCurrentLanguage('da');
    document.documentElement.lang = 'da';
    localStorage.setItem('language', 'da');
    console.log('LanguageProvider initialized with Danish');
    
    // Force resource reload to ensure translations are applied
    i18n.reloadResources('da');
  }, []);

  // Listen for language changes from i18next
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      // Only allow Danish
      if (lng !== 'da') {
        console.log('Forcing language back to Danish from:', lng);
        i18n.changeLanguage('da');
        setCurrentLanguage('da');
        localStorage.setItem('language', 'da');
        document.documentElement.lang = 'da';
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  return (
    <LanguageContext.Provider value={{ 
      currentLanguage, 
      setLanguage,
      supportedLanguages: allSupportedLanguages
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
